import { defineStore } from 'pinia';
import { getReadingDate } from '../utils/stats';

export const useProgressStore = defineStore('progress', {
    state: () => ({
        // map[planId] = { completedDays: [1, 2, 5], lastUpdated: ISOString }
        progressByPlanId: {}
    }),
    actions: {
        init() {
            const saved = localStorage.getItem('readingProgress');
            if (saved) {
                try {
                    this.progressByPlanId = JSON.parse(saved);
                } catch (e) {
                    console.error("Failed to load progress", e);
                }
            }
        },
        async toggleDayCompletion(planId, day, scheduleType = 'daily') {
            if (!this.progressByPlanId[planId]) {
                this.progressByPlanId[planId] = {
                    completedDays: [],
                    completedDates: {}, // Map dayNum -> YYYY-MM-DD
                    lastUpdated: new Date().toISOString(),
                    lastCompletedAt: null,
                    scheduleShift: null
                };
            }

            const record = this.progressByPlanId[planId];
            if (!record.completedDates) record.completedDates = {}; // Ensure struct exists
            if (record.scheduleShift === undefined) record.scheduleShift = null;

            const idx = record.completedDays.indexOf(day);
            const now = new Date();
            const nowIso = now.toISOString();
            const completionDate = record.startDate
                ? getReadingDate(record.startDate, day, scheduleType)
                : nowIso.split('T')[0];

            if (idx > -1) {
                record.completedDays.splice(idx, 1);
                delete record.completedDates[day];
            } else {
                record.completedDays.push(day);
                record.completedDates[day] = completionDate;
                record.lastCompletedAt = completionDate;
            }

            if (idx > -1) {
                const remainingDates = Object.values(record.completedDates || {});
                record.lastCompletedAt = remainingDates.length
                    ? remainingDates.sort().slice(-1)[0]
                    : null;
            }

            record.lastUpdated = nowIso;
            this.save();
        },
        isDayCompleted(planId, day) {
            const record = this.progressByPlanId[planId];
            if (!record) return false;
            return record.completedDays.includes(day);
        },
        getCompletedCount(planId) {
            return this.progressByPlanId[planId]?.completedDays.length || 0;
        },
        getStartDate(planId) {
            return this.progressByPlanId[planId]?.startDate || null;
        },
        setStartDate(planId, date) {
            if (!this.progressByPlanId[planId]) {
                this.progressByPlanId[planId] = {
                    completedDays: [],
                    completedDates: {},
                    lastUpdated: new Date().toISOString(),
                    lastCompletedAt: null,
                    scheduleShift: null
                };
            }
            this.progressByPlanId[planId].startDate = date;
            this.progressByPlanId[planId].lastUpdated = new Date().toISOString();
            this.save();
        },
        getScheduleShift(planId) {
            return this.progressByPlanId[planId]?.scheduleShift || null;
        },
        setScheduleShift(planId, anchor) {
            if (!this.progressByPlanId[planId]) {
                this.progressByPlanId[planId] = {
                    completedDays: [],
                    completedDates: {},
                    lastUpdated: new Date().toISOString(),
                    lastCompletedAt: null,
                    scheduleShift: null
                };
            }
            this.progressByPlanId[planId].scheduleShift = anchor;
            this.progressByPlanId[planId].lastUpdated = new Date().toISOString();
            this.save();
        },
        clearScheduleShift(planId) {
            if (!this.progressByPlanId[planId]) return;
            this.progressByPlanId[planId].scheduleShift = null;
            this.progressByPlanId[planId].lastUpdated = new Date().toISOString();
            this.save();
        },
        async save() {
            localStorage.setItem('readingProgress', JSON.stringify(this.progressByPlanId));

            // Sync with AuthStore (Firestore)
            const { useAuthStore } = await import('./auth');
            const authStore = useAuthStore();
            if (authStore.isAuthenticated) {
                authStore.updateProgress(this.progressByPlanId);
            }
        },
        async migrateLocalToFirestore(remoteProgress) {
            // 1. If remote has data, merge it in (Remote wins on conflicts usually, or union)
            // 2. If remote is empty, push local

            // Simple union merge for completedDays
            for (const [planId, remoteData] of Object.entries(remoteProgress || {})) {
                if (!this.progressByPlanId[planId]) {
                    this.progressByPlanId[planId] = remoteData;
                    continue;
                }

                const local = this.progressByPlanId[planId];
                // Merge days
                const allDays = new Set([...local.completedDays, ...(remoteData.completedDays || [])]);
                local.completedDays = Array.from(allDays).sort((a, b) => a - b);

                // Merge dates (Remote wins if exists, else keep local, else derive?)
                local.completedDates = { ...(local.completedDates || {}), ...(remoteData.completedDates || {}) };

                // TODO: If we have completedDays without corresponding completedDates and a startDate is set,
                //       we may want to derive calendar dates for those days based on the plan start date
                //       to support date-based statistics. This backfilling is intentionally not performed
                //       here and should be handled in stats calculation logic or in a dedicated migration
                //       step if/when the requirement is implemented.

                // Update metadata
                if (remoteData.startDate && !local.startDate) local.startDate = remoteData.startDate;
                if (remoteData.scheduleShift && !local.scheduleShift) {
                    local.scheduleShift = remoteData.scheduleShift;
                } else if (remoteData.scheduleShift && local.scheduleShift) {
                    if (remoteData.lastUpdated && remoteData.lastUpdated > local.lastUpdated) {
                        local.scheduleShift = remoteData.scheduleShift;
                    }
                }
                if (remoteData.lastUpdated) {
                    if (!local.lastUpdated || remoteData.lastUpdated > local.lastUpdated) {
                        local.lastUpdated = remoteData.lastUpdated;
                    }
                }
                if (!local.lastCompletedAt && local.completedDates) {
                    const latest = Object.values(local.completedDates).sort().slice(-1)[0];
                    local.lastCompletedAt = latest || null;
                }
            }

            this.save(); // Takes Combined and pushes back to Firestore
        }
    }
});
